import React, { ComponentProps, KeyboardEvent, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Box } from '@mui/material';

import {
  DatePickerProps,
  FormLabel,
  TextFieldProps,
  TimePicker,
  useWindowedTooltipZIndex,
  withWindowedPopperProps,
} from '@sbv-clients/ui-shared';
import { addHours, subHours } from '@sbv-clients/util-misc';

import { useFormFieldError } from '../../hooks/use-field-error';

type PickerPropsType = {
  onOpen?: () => void;
};

type TextFieldPropsType = {
  name: string;
  label?: TextFieldProps['label'];
  fullWidth?: TextFieldProps['fullWidth'];
  disabled?: TextFieldProps['disabled'];
  required?: TextFieldProps['required'];
  size?: TextFieldProps['size'];
  placeholder?: TextFieldProps['placeholder'];
  onChange?: (value: Date | null) => void;
};

type FormTimePickerFieldProps = PickerPropsType & TextFieldPropsType;
type RenderType = ComponentProps<typeof Controller>['render'];
export const FormTimePickerField = ({
  name,
  label,
  fullWidth,
  disabled,
  required,
  size,
  onOpen,
  placeholder,
  onChange,
}: FormTimePickerFieldProps) => {
  const { error, helperText } = useFormFieldError(name);
  const { setValue, getValues } = useFormContext();
  const windowedConfig = useWindowedTooltipZIndex();

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const time = getValues(name);
      if (event.key === 'p' || event.key === 'P') {
        setValue(name, addHours(time, 12));
      }
      if (event.key === 'a' || event.key === 'A') {
        setValue(name, subHours(time, 12));
      }
    },
    [name, getValues, setValue]
  );

  const render: RenderType = useCallback(
    ({ field }) => {
      return (
        <Box>
          {!!label && (
            <FormLabel>
              {label}
              {required && <span style={{ color: 'red' }}> * </span>}
            </FormLabel>
          )}
          <TimePicker
            disabled={disabled}
            onOpen={onOpen}
            {...field}
            onChange={(value) => {
              field.onChange(value);
              onChange?.(value);
            }}
            slotProps={{
              textField: {
                fullWidth,
                required,
                size,
                helperText,
                error,
                onKeyDown,
                placeholder,
                variant: 'outlined',
              },
              popper: withWindowedPopperProps(windowedConfig, {
                sx: {
                  '& .MuiList-root': {
                    // see https://sbvwm.atlassian.net/browse/SCHD-116
                    // also can hide scrollbar scrollbarWidth: 'none',
                    width: '80px',
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    justifyContent: 'center',
                    alignItems: 'center' /* Center horizontally */,
                    overflowY: 'scroll',
                  },
                },
              }),
            }}
          />
        </Box>
      );
    },
    [
      disabled,
      error,
      fullWidth,
      helperText,
      label,
      onKeyDown,
      onOpen,
      required,
      size,
      onChange,
      placeholder,
      windowedConfig,
    ]
  );

  return <Controller name={name} render={render} />;
};
